import useScheduleRealtime from '../../realtime/useScheduleRealtime';
import React, { useState, useEffect, useMemo } from 'react';
import { Form, Button, Table, Badge, Container, Row, Col, Card, Modal } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import EditJobModal from './EditJobModal';
// Import centralized date utilities
import { formatDateLocalEnhanced } from '../../utils/dateUtils';
import { getProjectStatus } from '../../utils/dataNormalization';
import { fetchData } from '../../redux/slices/apiCallSlice';
import { selectApiUrls } from '../../redux/slices/apiUrlSlice';

const JobScheduleSearch = ({ filteredJobs = null, canEdit = true }) => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchField, setSearchField] = useState('all');
  const [searchResults, setSearchResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [fullScheduleJobs, setFullScheduleJobs] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [submittedSearch, setSubmittedSearch] = useState(null);
    // Always call hooks unconditionally
  const jobScheduleDataFromRedux = useSelector((state) => state.jobSchedule.data);
  const apiUrls = useSelector(selectApiUrls);

  useEffect(() => {
    let isMounted = true;

    const loadFullScheduleJobs = async () => {
      if (!apiUrls?.fullSchedule) {
        return;
      }

      setLoadingSearch(true);
      try {
        const response = await dispatch(fetchData({ url: apiUrls.fullSchedule })).unwrap();
        const raw = response?.data || response || {};
        const normalizedJobs = Array.isArray(raw)
          ? raw
          : Object.values(raw)
            .map((item) => item?.GeneralProjectInfo)
            .filter(Boolean);

        if (isMounted) {
          setFullScheduleJobs(normalizedJobs);
        }
      } catch (error) {
        console.error('JobScheduleSearch: Failed to load full schedule search dataset:', error);
      } finally {
        if (isMounted) setLoadingSearch(false);
      }
    };

    loadFullScheduleJobs();

    return () => {
      isMounted = false;
    };
  }, [dispatch, apiUrls?.fullSchedule]);

  // Search should use full-schedule with no URL params; fall back to existing sources only if unavailable.
  const jobScheduleData = useMemo(() => {
    if (fullScheduleJobs.length > 0) {
      return fullScheduleJobs;
    }

    return filteredJobs || jobScheduleDataFromRedux || [];
  }, [fullScheduleJobs, filteredJobs, jobScheduleDataFromRedux]);
  
  useScheduleRealtime({ view: 'search', blocked: showEditModal || loadingSearch, apply: rows => {
    setFullScheduleJobs(rows);
    if (submittedSearch) {
      const { term, field } = submittedSearch;
      const fields = field === 'all' ? ['ModelNumber', 'JobNumber', 'ProjectName', 'ID'] : [field];
      setSearchResults(term ? rows.filter(job => fields.some(key => String(job[key] ?? '').toLowerCase().includes(term))) : []);
    }
  } });

  // Handle opening edit modal
  const handleOpenEditModal = (job) => {
    if (!canEdit) {
      return;
    }

    setSelectedJob(job);
    setShowEditModal(true);
  };
  
  // Handle closing edit modal
  const handleCloseEditModal = () => {
    setShowEditModal(false);
  };
  
  // Handle job save
  const handleSaveJob = () => {
    // Refresh results if needed
    if (hasSearched && searchTerm) {
      handleSearch({ preventDefault: () => {} });
    }  };
  
  // Handle search field change
  const handleSearchFieldChange = (e) => {
    setSearchField(e.target.value);
  };
  
  // Handle search term change
  const handleSearchTermChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // Perform search
  const handleSearch = (e) => {
    e.preventDefault();
    setSubmittedSearch({ term: searchTerm.toLowerCase().trim(), field: searchField });
    
    if (!searchTerm.trim()) {
      setSearchResults([]);
      setHasSearched(true);
      return;
    }
    
    const term = searchTerm.toLowerCase().trim();
    
    let results;
    if (searchField === 'all') {
      results = jobScheduleData.filter(job => 
        (job.ModelNumber && job.ModelNumber.toString().toLowerCase().includes(term)) ||
        (job.JobNumber && job.JobNumber.toString().toLowerCase().includes(term)) ||
        (job.ProjectName && job.ProjectName.toString().toLowerCase().includes(term)) ||
        (job.ID && job.ID.toString().toLowerCase().includes(term))
      );
    } else {
      results = jobScheduleData.filter(job => 
        job[searchField] && job[searchField].toString().toLowerCase().includes(term)
      );
    }
    
    setSearchResults(results);
    setHasSearched(true);
  };
    // Format date for display
  const formatDate = (dateString) => {
    return formatDateLocalEnhanced(dateString);
  };
  
  // Get status badge
  const getStatusBadge = (status) => {
    if (!status) return <Badge bg="secondary">Unknown</Badge>;
    
    if (status.includes('Hold')) return <Badge bg="warning">On Hold</Badge>;
    if (status.includes('Cancel')) return <Badge bg="danger">canceled</Badge>;
    if (status.includes('Shipped')) return <Badge bg="success">Shipped</Badge>;
    
    return <Badge bg="primary">{status}</Badge>;
  };
  
  return (
    <Container fluid>
      <Card className="mb-4">
        <Card.Header as="h5">Advanced Search</Card.Header>
        <Card.Body>
          <Form onSubmit={handleSearch}>
            <Row className="align-items-end">
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Search Field</Form.Label>
                  <Form.Select value={searchField} onChange={handleSearchFieldChange}>
                    <option value="all">All Fields</option>
                    <option value="ModelNumber">Model Number</option>
                    <option value="JobNumber">Job Number</option>
                    <option value="ProjectName">Project Name</option>
                    <option value="ID">ID</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Search Term</Form.Label>
                  <Form.Control 
                    type="text" 
                    placeholder="Enter search term..." 
                    value={searchTerm} 
                    onChange={handleSearchTermChange}
                  />
                </Form.Group>
              </Col>
              
              <Col md={2}>
                <Button variant="primary" type="submit" className="w-100 mb-3">
                  Search
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>
      
      {hasSearched && (
        <div>
          <h5>Search Results: {searchResults.length} {searchResults.length === 1 ? 'job' : 'jobs'} found</h5>
          
          {searchResults.length > 0 ? (
            <Table striped bordered hover responsive className="mt-3 schedule-responsive-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Job Number</th>
                  <th>Project Name</th>
                  <th>Model Number</th>
                  <th>Schedule Status</th>
                  <th>Portal Status</th>
                  <th>Current Ship Date</th>
                </tr>
              </thead>
              <tbody>
                {searchResults.map(job => (
                  <tr 
                    key={job.ID} 
                    onDoubleClick={() => handleOpenEditModal(job)}
                    className={canEdit ? "cursor-pointer" : ""}
                    style={{ cursor: canEdit ? 'pointer' : 'default' }}
                    title={canEdit ? 'Double-click to edit' : ''}
                  >
                    <td data-label="ID">{job.ID}</td>
                    <td data-label="Job Number">{job.JobNumber || 'N/A'}</td>
                    <td data-label="Project Name">{job.ProjectName || 'N/A'}</td>
                    <td data-label="Model Number">{job.ModelNumber || 'N/A'}</td>
                    <td data-label="Schedule Status">{getStatusBadge(job.JobStatus)}</td>
                    <td data-label="Portal Status">{getStatusBadge(getProjectStatus(job))}</td>
                    <td data-label="Current Ship Date">{formatDate(job.CurrentPlanShipDate)}</td>
                    {canEdit && (
                      <td data-label="Actions" className="schedule-card-action-cell">
                        <button type="button" className="btn btn-primary schedule-card-action" onClick={() => handleOpenEditModal(job)}>
                          Open job
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="alert alert-info">No matching jobs found.</div>
          )}
        </div>
      )}
      
      {/* Modal wrapper for EditJobModal */}
      <Modal className="job-schedule-modal" show={showEditModal} onHide={handleCloseEditModal} size="xl">
        {selectedJob && (
          <EditJobModal
            selectedJob={selectedJob}
            setSelectedJob={setSelectedJob}
            handleCloseModal={handleCloseEditModal}
            handleSaveChanges={handleSaveJob}
            canEdit={canEdit}
          />
        )}
      </Modal>
    </Container>
  );
};

export default JobScheduleSearch;
